// ============================================================================
// MILES — Guides éditoriaux : accès au snapshot CMS (guidesContent.json exporté
// par le backend à chaque enregistrement) + univers éditoriaux + copy des pages.
// Prerender-safe : aucun accès à window.
// ============================================================================
import SNAPSHOT from "./guidesContent.json";
import { BASE_URL, BOOKING_PATH, LANGS, p } from "./seoContent";

export const GUIDES_SLUG = { fr: "guides", en: "guides", de: "ratgeber" };
export const ABOUT_SLUG = { fr: "a-propos", en: "about", de: "ueber-uns" };
export const MIN_CLUSTER_GUIDES = 3; // page univers indexable seulement si ≥ 3 guides publiés

export const CLUSTERS = {
  airports: {
    slug: { fr: "aeroports", en: "airports", de: "flughaefen" },
    name: { fr: "Aéroports suisses", en: "Swiss airports", de: "Schweizer Flughäfen" },
    intro: {
      fr: "Genève, Zurich, Bâle-Mulhouse : terminaux, points de rencontre, temps à prévoir, gestion des retards et organisation d'un transfert avec chauffeur.",
      en: "Geneva, Zurich, Basel-Mulhouse: terminals, meeting points, time to allow, delays and how a chauffeur transfer actually works.",
      de: "Genf, Zürich, Basel-Mulhouse: Terminals, Treffpunkte, Zeitpuffer, Verspätungen und der Ablauf eines Transfers mit Chauffeur.",
    },
  },
  routes: {
    slug: { fr: "trajets", en: "routes", de: "strecken" },
    name: { fr: "Trajets & destinations", en: "Routes & destinations", de: "Strecken & Ziele" },
    intro: {
      fr: "Distances, durées réelles, alternatives train/taxi/chauffeur et particularités saisonnières des trajets réellement assurés par MILES : Lausanne, Montreux, Verbier, Gstaad, Chamonix, Saint-Moritz…",
      en: "Distances, realistic drive times, train/taxi/chauffeur alternatives and seasonal specifics for the routes MILES actually drives: Lausanne, Montreux, Verbier, Gstaad, Chamonix, St. Moritz…",
      de: "Distanzen, realistische Fahrzeiten, Alternativen (Zug/Taxi/Chauffeur) und saisonale Besonderheiten der Strecken, die MILES tatsächlich fährt: Lausanne, Montreux, Verbier, Gstaad, Chamonix, St. Moritz…",
    },
  },
  cities: {
    slug: { fr: "villes", en: "cities", de: "staedte" },
    name: { fr: "Villes & mobilité locale", en: "Cities & local mobility", de: "Städte & lokale Mobilität" },
    intro: {
      fr: "Se déplacer à Genève, Lausanne, Montreux, Zurich ou Sion : options, temps de trajet, événements et quand un chauffeur privé a du sens.",
      en: "Getting around Geneva, Lausanne, Montreux, Zurich or Sion: options, travel times, events and when a private chauffeur makes sense.",
      de: "Unterwegs in Genf, Lausanne, Montreux, Zürich oder Sitten: Optionen, Fahrzeiten, Events und wann ein privater Chauffeur sinnvoll ist.",
    },
  },
  business: {
    slug: { fr: "business", en: "business", de: "business" },
    name: { fr: "Déplacements professionnels", en: "Business travel", de: "Geschäftsreisen" },
    intro: {
      fr: "Organiser les trajets de dirigeants, collaborateurs, clients et invités : réservation pour des tiers, facturation centralisée, hôtels, conciergeries, cliniques et événements.",
      en: "Organising rides for executives, teams, clients and guests: booking for others, centralised invoicing, hotels, concierges, clinics and events.",
      de: "Fahrten für Führungskräfte, Mitarbeitende, Kunden und Gäste organisieren: Buchung für Dritte, zentrale Abrechnung, Hotels, Concierges, Kliniken und Events.",
    },
  },
  travellers: {
    slug: { fr: "pratique", en: "practical", de: "praktisch" },
    name: { fr: "Guides pratiques voyageurs", en: "Practical traveller guides", de: "Praktische Reise-Ratgeber" },
    intro: {
      fr: "Enfants et sièges auto, groupes, bagages et skis, retards de vol, arrivée tardive, différences entre taxi, chauffeur privé, location et train.",
      en: "Children and car seats, groups, luggage and skis, flight delays, late arrivals, and the real differences between taxi, private chauffeur, rental and train.",
      de: "Kinder und Kindersitze, Gruppen, Gepäck und Ski, Flugverspätungen, späte Ankunft sowie die Unterschiede zwischen Taxi, Chauffeur, Mietwagen und Zug.",
    },
  },
  miles: {
    slug: { fr: "fonctionnement", en: "how-it-works", de: "so-funktioniert-es" },
    name: { fr: "Fonctionnement & expertise MILES", en: "How MILES works", de: "So funktioniert MILES" },
    intro: {
      fr: "Suivi de trajet en direct, suivi des vols, organisation des chauffeurs, sécurité, paiement et chaque étape d'une réservation, expliqués sans jargon.",
      en: "Live ride tracking, flight monitoring, how chauffeurs are organised, safety, payment and every step of a booking — explained plainly.",
      de: "Live-Tracking, Flugüberwachung, Organisation der Chauffeure, Sicherheit, Zahlung und jeder Schritt einer Buchung – verständlich erklärt.",
    },
  },
};

export const GUIDES_COPY = {
  fr: {
    eyebrow: "Guides MILES", title: "Guides MILES", h1: "Comprendre, comparer, organiser vos trajets en Suisse",
    seo_title: "Guides MILES — Transferts, aéroports et trajets avec chauffeur en Suisse",
    meta: "Guides pratiques rédigés par l'équipe MILES : aéroports suisses, trajets vers les Alpes, déplacements professionnels, voyage avec enfants ou skis. Informations vérifiées, réservation possible en un geste.",
    intro: "Des réponses concrètes aux questions que se posent les voyageurs avant de réserver un chauffeur : temps à prévoir, itinéraires, alternatives, coûts, particularités. Rédigé et vérifié par l'équipe MILES.",
    universes: "Univers", latest: "Derniers guides", all_in: "Tous les guides", read: "Lire le guide", read_next: "À lire ensuite",
    published: "Publié le", updated: "Mis à jour le", author: "Équipe éditoriale MILES", author_role: "Transport professionnel de personnes en Suisse",
    about_link: "Qui écrit ces guides ?", sources: "Sources", faq: "Questions fréquentes", summary: "L'essentiel", toc: "Sommaire",
    facts_title: "Ce trajet en bref", cta_booking: "Voir le prix de ce trajet", cta_book: "Réserver un chauffeur", cta_business: "Découvrir MILES Business",
    cta_hint: "Prix confirmé avant le départ, chauffeur identifié, suivi en direct.", back_hub: "Tous les guides",
    draft: "Brouillon — visible uniquement en aperçu, non indexé.", guides_count: (n) => `${n} guide${n > 1 ? "s" : ""}`,
    empty: "Les premiers guides de cet univers arrivent bientôt.", home: "Accueil",
    about_title: "Qui écrit les Guides MILES ?", about_seo: "Équipe éditoriale MILES — Politique éditoriale des guides",
    about_meta: "Qui rédige les guides MILES, comment les informations sont vérifiées et pourquoi une entreprise de transport avec chauffeur publie des guides pratiques.",
    other_langs: "Ce guide existe aussi en",
  },
  en: {
    eyebrow: "MILES Guides", title: "MILES Guides", h1: "Understand, compare and organise your journeys in Switzerland",
    seo_title: "MILES Guides — Transfers, airports and chauffeur routes in Switzerland",
    meta: "Practical guides written by the MILES team: Swiss airports, routes to the Alps, business travel, travelling with children or skis. Verified information, booking one tap away.",
    intro: "Straight answers to the questions travellers ask before booking a chauffeur: time to allow, routes, alternatives, costs, specifics. Written and checked by the MILES team.",
    universes: "Topics", latest: "Latest guides", all_in: "All guides", read: "Read the guide", read_next: "Read next",
    published: "Published", updated: "Updated", author: "MILES editorial team", author_role: "Professional passenger transport in Switzerland",
    about_link: "Who writes these guides?", sources: "Sources", faq: "Frequently asked questions", summary: "In short", toc: "Contents",
    facts_title: "This route at a glance", cta_booking: "See the price for this route", cta_book: "Book a chauffeur", cta_business: "Discover MILES Business",
    cta_hint: "Price confirmed before departure, identified chauffeur, live tracking.", back_hub: "All guides",
    draft: "Draft — preview only, not indexed.", guides_count: (n) => `${n} guide${n === 1 ? "" : "s"}`,
    empty: "The first guides in this topic are coming soon.", home: "Home",
    about_title: "Who writes the MILES Guides?", about_seo: "MILES editorial team — Editorial policy",
    about_meta: "Who writes the MILES guides, how information is verified and why a chauffeur company publishes practical guides.",
    other_langs: "Also available in",
  },
  de: {
    eyebrow: "MILES Ratgeber", title: "MILES Ratgeber", h1: "Verstehen, vergleichen und Fahrten in der Schweiz organisieren",
    seo_title: "MILES Ratgeber — Transfers, Flughäfen und Chauffeur-Strecken in der Schweiz",
    meta: "Praktische Ratgeber vom MILES-Team: Schweizer Flughäfen, Strecken in die Alpen, Geschäftsreisen, Reisen mit Kindern oder Ski. Geprüfte Informationen, Buchung mit einem Klick.",
    intro: "Klare Antworten auf die Fragen, die Reisende vor der Buchung eines Chauffeurs stellen: Zeitpuffer, Strecken, Alternativen, Kosten, Besonderheiten. Geschrieben und geprüft vom MILES-Team.",
    universes: "Themen", latest: "Neueste Ratgeber", all_in: "Alle Ratgeber", read: "Ratgeber lesen", read_next: "Weiterlesen",
    published: "Veröffentlicht am", updated: "Aktualisiert am", author: "MILES Redaktionsteam", author_role: "Professioneller Personentransport in der Schweiz",
    about_link: "Wer schreibt diese Ratgeber?", sources: "Quellen", faq: "Häufige Fragen", summary: "Das Wichtigste", toc: "Inhalt",
    facts_title: "Diese Strecke auf einen Blick", cta_booking: "Preis für diese Strecke ansehen", cta_book: "Chauffeur buchen", cta_business: "MILES Business entdecken",
    cta_hint: "Preis vor der Abfahrt bestätigt, identifizierter Chauffeur, Live-Tracking.", back_hub: "Alle Ratgeber",
    draft: "Entwurf — nur Vorschau, nicht indexiert.", guides_count: (n) => `${n} Ratgeber`,
    empty: "Die ersten Ratgeber zu diesem Thema folgen bald.", home: "Startseite",
    about_title: "Wer schreibt die MILES Ratgeber?", about_seo: "MILES Redaktionsteam — Redaktionelle Grundsätze",
    about_meta: "Wer die MILES-Ratgeber schreibt, wie Informationen geprüft werden und warum ein Chauffeurunternehmen praktische Ratgeber veröffentlicht.",
    other_langs: "Auch verfügbar in",
  },
};

// ---------------------------------------------------------------- accès données
export const ALL_GUIDES = SNAPSHOT.guides || [];
export const publishedGuides = (lang) => ALL_GUIDES.filter((g) => g.lang === lang && g.status === "published")
  .sort((a, b) => String(b.published_at || "").localeCompare(String(a.published_at || "")));
export const guidesInCluster = (lang, clusterId) => publishedGuides(lang).filter((g) => g.category === clusterId);
export const guideBySlug = (lang, slug) => ALL_GUIDES.find((g) => g.lang === lang && g.slug === slug)
  || ALL_GUIDES.find((g) => g.lang === lang && (g.redirects_from || []).includes(slug)) || null;
export const guideById = (id) => ALL_GUIDES.find((g) => g.id === id) || null;

export const guidesHubUrl = (lang) => p(lang, GUIDES_SLUG[lang]);
export const clusterUrl = (lang, clusterId) => p(lang, GUIDES_SLUG[lang], CLUSTERS[clusterId].slug[lang]);
export const aboutUrl = (lang) => p(lang, GUIDES_SLUG[lang], ABOUT_SLUG[lang]);
export const guideUrl = (g) => p(g.lang, GUIDES_SLUG[g.lang], g.slug);
export const clusterBySlug = (lang, slug) => Object.keys(CLUSTERS).find((k) => CLUSTERS[k].slug[lang] === slug) || null;
export const clusterIndexable = (lang, clusterId) => guidesInCluster(lang, clusterId).length >= MIN_CLUSTER_GUIDES;
export const hubIndexable = (lang) => publishedGuides(lang).length >= 1;

// hreflang : uniquement entre variantes RÉELLEMENT équivalentes (même translation_key), publiées
export function guideAlternates(g) {
  const out = {};
  for (const l of LANGS) {
    const v = ALL_GUIDES.find((x) => x.translation_key === g.translation_key && x.lang === l && x.status === "published");
    if (v) out[l] = guideUrl(v);
    else if (g.hreflang_urls?.[l]) out[l] = g.hreflang_urls[l];
  }
  out[g.lang] = guideUrl(g); // auto-référence toujours
  return out;
}

// guides publiés qui soutiennent une landing page commerciale (maillage landing → guides)
export const guidesForLanding = (lang, path, limit = 4) => publishedGuides(lang)
  .filter((g) => g.landing_page && g.landing_page.replace(/\/$/, "") === String(path).replace(/\/$/, ""))
  .slice(0, limit);

// « À lire ensuite » : liés explicites, puis même univers, puis récents
export function nextReads(g, limit = 3) {
  const pub = publishedGuides(g.lang).filter((x) => x.id !== g.id);
  const explicit = (g.related_ids || []).map((id) => pub.find((x) => x.id === id)).filter(Boolean);
  const same = pub.filter((x) => x.category === g.category && !explicit.includes(x));
  const rest = pub.filter((x) => !explicit.includes(x) && !same.includes(x));
  return [...explicit, ...same, ...rest].slice(0, limit);
}

// CTA réservation préremplie (départ/destination + coordonnées), attribuée au guide
export function guideBookingHref(g) {
  const params = new URLSearchParams({ type: "one_way", src: `guide:${g.id}` });
  if (g.booking_from) params.set("from", g.booking_from);
  if (g.booking_to) params.set("to", g.booking_to);
  if (g.booking_from_coords?.length === 2) { params.set("from_lat", g.booking_from_coords[0]); params.set("from_lon", g.booking_from_coords[1]); }
  if (g.booking_to_coords?.length === 2) { params.set("to_lat", g.booking_to_coords[0]); params.set("to_lon", g.booking_to_coords[1]); }
  return `${BOOKING_PATH}?${params.toString()}`;
}

export const guideCanonical = (g) => g.canonical?.trim() || `${BASE_URL}${guideUrl(g)}`;
export const formatDate = (iso, lang) => {
  if (!iso) return "";
  const d = new Date(iso);
  const loc = { fr: "fr-CH", en: "en-GB", de: "de-CH" }[lang] || "fr-CH";
  return new Intl.DateTimeFormat(loc, { day: "numeric", month: "long", year: "numeric", timeZone: "Europe/Zurich" }).format(d);
};
export const dateOnly = (iso) => (iso ? String(iso).slice(0, 10) : "");

// switcher de langue : équivalent d'un chemin /xx/guides/... dans une autre langue
export function guideAlternatePath(fromLang, sub, targetLang) {
  if (!sub) return guidesHubUrl(targetLang);
  if (sub === ABOUT_SLUG[fromLang]) return aboutUrl(targetLang);
  const cl = clusterBySlug(fromLang, sub);
  if (cl) return clusterUrl(targetLang, cl);
  const g = guideBySlug(fromLang, sub);
  if (g) return guideAlternates(g)[targetLang] || guidesHubUrl(targetLang);
  return guidesHubUrl(targetLang);
}
